import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react'
import useQueryGetUserDetail from '@/api/auth/getUser.ts'
import useQueryGetUserRecentState from '@/api/user/getUserRecentState.ts'
import { useNavigate } from 'react-router-dom'
import BiometricDashboard from '@/pages/users/detail/BiometricDashboard.tsx'
import UserDetailMap from '@/pages/users/UserDetailMap.tsx'
import { format } from 'date-fns'
import { calculateAge } from '@/util/person.ts'
import ContactBadge from '@/component/badge/ContactBadge.tsx'
import useQueryGetUserStatAverage from '@/api/user/getUserStatAverage.ts'
import AlertStatusBadge from '@/component/badge/AlertStatusBadge.tsx'
import BatteryStatusBadge from '@/component/badge/BatteryStatusBadge.tsx'
import { useTranslation } from 'react-i18next'
import useQueryGetMe from '@/api/auth/getMe.ts'
import { DeviceStatus } from '@/api/user/getUsers.ts'
import useQueryGetUserChart from '@/api/user/getUserChart.ts'
import useQueryGetUserChart10Minutes from '@/api/user/getUserChart10Minutes.ts'
import Select, { ISelectOption } from '@/component/input/Select.tsx'
import { apiPatchDeviceSetting } from '@/api/auth/patchDeviceSetting.ts'
import { apiGetCurrentSetting } from '@/api/auth/getCurrentSetting.ts'
import { useMutation, useQuery } from '@tanstack/react-query'


export interface IProps {
    id?: number
    className?: string
    onRefresh: () => void
}

const UserDetail: React.FC<IProps> = ({ className, id = 0 }) => {
    const { t } = useTranslation()

    const { data: user } = useQueryGetUserDetail(id)
    const { data: recentState } = useQueryGetUserRecentState(id)
    const containerRef = useRef<any>(null)
    const navigate = useNavigate()
    const { data: averageStats } = useQueryGetUserStatAverage(id)
    const { data: me } = useQueryGetMe()

    // 차트 데이터 API 호출
    const { data: dailyChartData } = useQueryGetUserChart(id)
    const { data: realtimeChartData } = useQueryGetUserChart10Minutes(id)

    // 현재 설정 조회
    const { data: currentSetting, refetch: refetchSetting, isLoading: isCurrentSettingLoading } = useQuery({
        queryKey: ['currentSetting'],
        queryFn: apiGetCurrentSetting,
        enabled: !!me, // 사용자 정보가 있을 때만 실행
    })

    // 센싱주기 설정 상태
    const [sensingInterval, setSensingInterval] = useState<ISelectOption>()

    // 센싱주기 옵션 목록 (2분부터 1분 단위로 120분까지)
    const sensingIntervalOptions: ISelectOption[] = useMemo(() => {
        const options: ISelectOption[] = []
        for (let i = 2; i <= 120; i++) {
            options.push({
                id: i.toString(),
                text: t('users.detail.setting.sensingInterval.minuteFormat', { count: i }),
                value: i
            })
        }
        return options
    }, [t])

    // 디바이스 설정 업데이트 mutation
    const deviceSettingMutation = useMutation({
        mutationFn: apiPatchDeviceSetting,
        onSuccess: (data) => {
            if (Number(data.code) === 200) {
                // 설정 새로고침
                refetchSetting()
                // 성공 시 3초 후 성공 메시지 자동 숨김
                setTimeout(() => {
                    deviceSettingMutation.reset()
                }, 3000)
            } else {
                // 서버 에러로 처리
                throw new Error(`${t('error.serverError')}: ${data.message}`)
            }
        },
        onError: () => {
            // 에러 시 5초 후 에러 메시지 자동 숨김  
            setTimeout(() => {
                deviceSettingMutation.reset()
            }, 5000)
        }
    })

    // 센싱주기 변경 핸들러
    const handleSensingIntervalChange = (option: ISelectOption) => {
        setSensingInterval(option)

        // 현재 설정 로딩 중이면 처리하지 않음
        if (isCurrentSettingLoading) {
            return
        }

        // 현재 설정값을 기반으로 센싱주기만 변경해서 API 호출
        if (currentSetting?.data) {
            deviceSettingMutation.mutate({
                safeZoneRadiusM: currentSetting.data.safeZoneRadiusM,
                isAppPush: currentSetting.data.isAppPush,
                contact: currentSetting.data.contact,
                address: currentSetting.data.address,
                latitude: currentSetting.data.latitude,
                longitude: currentSetting.data.longitude,
                dataIntervalMinute: option.value
            })
        } else {
            // 현재 설정이 없으면 기본값과 함께 전송
            deviceSettingMutation.mutate({
                safeZoneRadiusM: 250, // 기본 안전구역 반경 250m
                isAppPush: true, // 기본 푸시 알림 활성화
                contact: '', // 빈 연락처
                address: '', // 빈 주소
                latitude: 37.5665, // 서울 위도 기본값
                longitude: 126.9780, // 서울 경도 기본값
                dataIntervalMinute: option.value
            })
        }
    }

    // 초기 센싱주기 설정 (서버에서 가져온 값 또는 기본값: 10분)
    useEffect(() => {
        // 현재 설정이 로딩 중이면 대기
        if (isCurrentSettingLoading) {
            return
        }

        // 옵션이 준비되지 않았으면 대기
        if (sensingIntervalOptions.length === 0) {
            return
        }

        // 이미 설정되어 있고, 서버 값과 일치하면 중복 설정 방지
        if (sensingInterval && currentSetting?.data?.dataIntervalMinute === sensingInterval.value) {
            return
        }

        if (currentSetting?.data?.dataIntervalMinute !== undefined && currentSetting.data.dataIntervalMinute !== null) {
            // 서버에서 가져온 현재 설정값으로 초기화
            const serverValue = Number(currentSetting.data.dataIntervalMinute)

            // 유효한 숫자인지 확인
            if (isNaN(serverValue) || serverValue <= 0) {
                const defaultOption = sensingIntervalOptions.find(option => option.value === 10)
                setSensingInterval(defaultOption || sensingIntervalOptions[8])
                return
            }

            const currentOption = sensingIntervalOptions.find(option => option.value === serverValue)
            if (currentOption) {
                setSensingInterval(currentOption)
            } else {
                // 서버 값이 옵션에 없으면 가장 가까운 값으로 설정하거나 기본값 사용
                if (serverValue >= 2 && serverValue <= 120) {
                    // 서버 값을 옵션에 임시로 추가
                    const tempOption = { id: serverValue.toString(), text: t('users.detail.setting.sensingInterval.minuteFormat', { count: serverValue }), value: serverValue }
                    setSensingInterval(tempOption)
                } else {
                    // 범위를 벗어나면 기본값 사용
                    const defaultOption = sensingIntervalOptions.find(option => option.value === 10)
                    setSensingInterval(defaultOption || sensingIntervalOptions[8])
                }
            }
        } else {
            // 서버 설정이 없으면 기본값 (10분) 설정
            const defaultOption = sensingIntervalOptions.find(option => option.value === 10)
            setSensingInterval(defaultOption || sensingIntervalOptions[8])
        }
    }, [currentSetting, sensingIntervalOptions, sensingInterval, isCurrentSettingLoading])

    // 데이터 변환 함수 (string x -> number x)
    const convertToNumericX = (data?: any[]) => {
        if (!data) return undefined
        return data.map(item => ({
            ...item,
            x: new Date(item.x).getTime() / 1000 // Unix timestamp로 변환
        }))
    }

    // 특정 이메일 계정인지 확인
    const isMaskingRequired = useMemo(() => {
        return me?.email === 'dbd_cm@clomental.com'
    }, [me?.email])

    // 마스킹 처리 함수
    const getMaskedName = (name?: string) => {
        if (!isMaskingRequired || !name) return name
        if (name.length <= 1) return name
        return name.charAt(0) + '**'
    }

    const getMaskedContact = (contact?: string) => {
        if (!isMaskingRequired || !contact) return contact

        // 모든 전화번호 앞 3자리만 보여주고 나머지는 * 처리
        const contactParts = contact.split('-')
        if (contactParts.length === 3) {
            return `${contactParts[0]}-****-****`
        }

        // 기타 패턴의 전화번호 처리
        if (contact.length > 3) {
            return contact.substring(0, 3) + contact.substring(3).replace(/./g, '*')
        }

        return contact
    }

    const getMaskedBirthDate = (birthDate?: string) => {
        if (!isMaskingRequired || !birthDate) return birthDate ? format(new Date(birthDate), 'yyyy-MM-dd') : ''
        return `19**-**-**`
    }

    const getMaskedAddress = (address?: string) => {
        if (!isMaskingRequired || !address) return address
        return '****'
    }

    const genderName = useMemo(() => {
        if (user?.gender == 0)
            return t('common.gender.male')
        if (user?.gender == 1)
            return t('common.gender.female')
        else
            return t('common.gender.none')
    }, [user?.gender])

    // 실시간 상태를 고려한 실제 디바이스 상태 계산 (SearchUserItem과 동일한 로직)
    const actualDeviceStatus = useMemo(() => {
        if (!user?.device) return undefined

        // 실시간 충전 상태 우선 확인
        if (recentState?.summary?.isCharging) return DeviceStatus.CHARGING

        // 실시간 온라인 상태 확인
        if (recentState?.summary?.isOnline !== undefined) {
            return recentState.summary.isOnline ? DeviceStatus.ONLINE : DeviceStatus.OFFLINE
        }

        // 기본 device.status 사용
        return user.device.status
    }, [user?.device, recentState?.summary?.isCharging, recentState?.summary?.isOnline])

    useEffect(() => {
        containerRef.current.scrollTo(0, 0)
        // 사용자가 변경될 때 센싱주기 설정 리셋
        setSensingInterval(undefined)
    }, [id])

    const getTime = (minutes?: number) => {
        const pastMinutes = minutes || 0
        const hour = Math.floor(pastMinutes / 60)
        const minute = Math.floor(pastMinutes % 60)
        return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
    }

    return (
        <div className={`${className} bg-sub_b p-[20px] h-full`} ref={containerRef}>
            {user && <>
                <div className={'w-full flex gap-x-[20px]'}>
                    <div className={'flex-1 flex flex-col gap-y-[10px]'}>
                        {/* 첫 번째 행: 회원정보, 사용시간 */}
                        <div className={'grid grid-cols-2 gap-x-[20px]'}>
                            <div className={'flex flex-col'}>
                                <p className={'text-left text-[14px] font-bold'}>{t('users.detail.info.title')}</p>
                                <div className={'flex-1 bg-white p-[20px] w-full flex flex-col gap-[10px] mt-[10px]'}>
                                    <div className={'flex gap-[10px]'}>
                                        <AlertStatusBadge value={user?.alertStatus} />
                                        <p className={'font-bold'}>{getMaskedName(user?.name)}</p>
                                    </div>
                                    <p>{format(new Date(user?.createdAt), 'yyyy')}-{user?.id}</p>
                                    <p className={''}>{genderName} / {isMaskingRequired ? getMaskedBirthDate(user?.birthDate) : format(new Date(user?.birthDate), 'yyyy-MM-dd')} ({t('users.detail.info.age', { count: calculateAge(user?.birthDate) })})</p>
                                    <p>{t('users.detail.info.address')} : {getMaskedAddress(user?.address) || t('common.none')}</p>
                                </div>
                            </div>
                            <div className={'flex flex-col'}>
                                <p className={'text-left text-[14px] font-bold'}>{t('users.detail.time.title')}</p>
                                <div
                                    className={'flex-1 bg-white p-[20px] w-full mt-[10px] text-[14px] text-center flex flex-col gap-[10px]'}>
                                    <div
                                        className={'w-full grid grid-cols-3 gap-[10px] border border-solid border-transparent border-b border-b-gray2 pb-[10px]'}>
                                        <p>{t('users.detail.time.table.period.column')}</p>
                                        <p>{t('users.detail.time.table.powerOn.column')}</p>
                                        <p>{t('users.detail.time.table.wearOn.column')}</p>
                                    </div>
                                    <div className={'w-full grid grid-cols-3'}>
                                        <p>{t('users.detail.time.table.period.recent24hours')}</p>
                                        <p>{getTime(averageStats?.devicePowerOnMinute?.day)}</p>
                                        <p>{getTime(averageStats?.deviceWearMinute?.day)}</p>
                                    </div>
                                    <div className={'w-full grid grid-cols-3'}>
                                        <p>{t('users.detail.time.table.period.recent7days')}</p>
                                        <p>{getTime(averageStats?.devicePowerOnMinute?.week)}</p>
                                        <p>{getTime(averageStats?.deviceWearMinute?.week)}</p>
                                    </div>
                                    <div className={'w-full grid grid-cols-3'}>
                                        <p>{t('users.detail.time.table.period.recent30days')}</p>
                                        <p>{getTime(averageStats?.devicePowerOnMinute?.month)}</p>
                                        <p>{getTime(averageStats?.deviceWearMinute?.month)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 두 번째 행: 연락처, 주의사항 */}
                        <div className={'grid grid-cols-2 gap-x-[20px]'}>
                            <div className={'flex flex-col'}>
                                <p className={'text-left text-[14px] font-bold'}>{t('users.detail.contact.title')}</p>
                                <div className={'flex-1 bg-white p-[20px] w-full mt-[10px] flex flex-col gap-[14px]'}>
                                    <div className={'flex gap-[20px]'}>
                                        <ContactBadge>{t('users.detail.contact.me')}</ContactBadge>
                                        <p>{getMaskedContact(user?.contact) || t('common.none')}</p>
                                    </div>
                                    <div className={'flex gap-[20px]'}>
                                        <ContactBadge>{t('users.detail.contact.protector')}</ContactBadge>
                                        <p>{getMaskedContact(user?.primaryContact) || t('common.none')}</p>
                                    </div>
                                    <div className={'flex gap-[20px]'}>
                                        <ContactBadge>{t('users.detail.contact.friend')}</ContactBadge>
                                        <p>{getMaskedContact(user?.secondaryContact) || t('common.none')}</p>
                                    </div>
                                    <div className={'flex gap-[20px]'}>
                                        <ContactBadge>{t('users.detail.contact.admin')}</ContactBadge>
                                        <p>{getMaskedContact(user?.tertiaryContact) || t('common.none')}</p>
                                    </div>
                                </div>
                            </div>
                            <div className={'flex flex-col'}>
                                <p className={'text-left text-[14px] font-bold'}>{t('users.detail.memo.title')}</p>
                                <div className={'flex-1 bg-white p-[20px] w-full mt-[10px] whitespace-pre-wrap'}>
                                    {user?.inspectorMemo}
                                </div>
                            </div>
                        </div>

                        {/* 세 번째 행: 센싱주기 설정 */}
                        <div className={'grid grid-cols-2 gap-x-[20px]'}>
                            <div className={'flex flex-col'}>
                                <div className={'flex items-center gap-2 mb-2'}>
                                    <span className={'text-[16px]'}>📡</span>
                                    <p className={'text-left text-[14px] font-bold'}>{t('users.detail.setting.sensingInterval.title')}</p>
                                </div>
                                <div className={'flex-1 bg-white p-[20px] w-full mt-[10px]'}>
                                    <div className={'flex flex-col gap-[16px]'}>
                                        <div className={'flex items-center justify-between'}>
                                            <span className={'text-[14px] font-medium text-gray-700'}>{t('users.detail.setting.sensingInterval.label')}</span>
                                            <div className={'w-[120px]'}>
                                                <Select
                                                    options={sensingIntervalOptions}
                                                    value={sensingInterval}
                                                    onChanged={handleSensingIntervalChange}
                                                    disabled={deviceSettingMutation.isPending || isCurrentSettingLoading}
                                                    className={''}
                                                />
                                            </div>
                                        </div>

                                        {/* API 상태 표시 */}
                                        {isCurrentSettingLoading && (
                                            <div className={'bg-blue-50 p-3 rounded-lg border-l-4 border-blue-400'}>
                                                <p className={'text-[12px] text-blue-700'}>
                                                    ⏳ {t('users.detail.setting.sensingInterval.status.loading')}
                                                </p>
                                            </div>
                                        )}

                                        {deviceSettingMutation.isPending && (
                                            <div className={'bg-yellow-50 p-3 rounded-lg border-l-4 border-yellow-400'}>
                                                <p className={'text-[12px] text-yellow-700'}>
                                                    ⏳ {t('users.detail.setting.sensingInterval.status.updating')}
                                                </p>
                                            </div>
                                        )}

                                        {deviceSettingMutation.isError && (
                                            <div className={'bg-red-50 p-3 rounded-lg border-l-4 border-red-400'}>
                                                <p className={'text-[12px] text-red-700'}>
                                                    ❌ {t('users.detail.setting.sensingInterval.status.error')}
                                                    <br />
                                                    <span className={'text-[11px] text-red-600'}>
                                                        {t('common.errorPrefix')} {deviceSettingMutation.error?.message || t('error.unknown')}
                                                    </span>
                                                </p>
                                            </div>
                                        )}

                                        {deviceSettingMutation.isSuccess && !deviceSettingMutation.isPending && (
                                            <div className={'bg-green-50 p-3 rounded-lg border-l-4 border-green-400'}>
                                                <p className={'text-[12px] text-green-700'}>
                                                    ✅ {t('users.detail.setting.sensingInterval.status.success')}
                                                </p>
                                            </div>
                                        )}

                                        {!isCurrentSettingLoading && !currentSetting && (
                                            <div className={'bg-orange-50 p-3 rounded-lg border-l-4 border-orange-400'}>
                                                <p className={'text-[12px] text-orange-700'}>
                                                    ⚠️ {t('users.detail.setting.sensingInterval.status.loadError')}
                                                </p>
                                            </div>
                                        )}

                                        <div className={'bg-blue-50 p-3 rounded-lg border-l-4 border-blue-400'}>
                                            <p className={'text-[12px] text-blue-700'}>
                                                ℹ️ {t('users.detail.setting.sensingInterval.description.line1')}
                                                <br />
                                                {t('users.detail.setting.sensingInterval.description.line2')}
                                            </p>
                                        </div>

                                        {sensingInterval && (
                                            <div className={'bg-green-50 p-3 rounded-lg border border-green-200'}>
                                                <p className={'text-[13px] text-green-700 font-medium'}>
                                                    {t('users.detail.setting.sensingInterval.currentSetting.prefix')} <span className={'font-bold'}>{sensingInterval.text}</span> {t('users.detail.setting.sensingInterval.currentSetting.suffix')}
                                                    {currentSetting?.data?.dataIntervalMinute && (
                                                        <>
                                                            <br />
                                                            <span className={'text-[11px] text-green-600'}>
                                                                {t('users.detail.setting.sensingInterval.currentSetting.serverValuePrefix')} {t('users.detail.setting.sensingInterval.minuteFormat', { count: currentSetting.data.dataIntervalMinute })}
                                                            </span>
                                                        </>
                                                    )}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {/* 빈 공간 (나중에 다른 설정 추가 가능) */}
                            <div className={'flex flex-col'}>
                                <div className={'flex items-center gap-2 mb-2'}>
                                    <span className={'text-[16px]'}>⚙️</span>
                                    <p className={'text-left text-[14px] font-bold'}>{t('users.detail.setting.additional.title')}</p>
                                </div>
                                <div className={'flex-1 bg-white p-[20px] w-full mt-[10px] flex items-center justify-center'}>
                                    <p className={'text-gray-400 text-[14px]'}>{t('users.detail.setting.additional.placeholder')}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={'w-[377px] flex flex-col gap-[10px]'}>
                        <div className={'flex-1 flex flex-col'}>
                            <div
                                className={'flex-1 flex justify-between items-center pl-[25px] pr-[6px] py-[11px] min-h-[48px] bg-white'}>
                                <p className={'text-left text-[14px] font-bold'}>{t('users.detail.location.title')}</p>
                                {user?.device &&
                                    <button className={'font-medium text-main_b px-[6px] py-[3px]'}
                                        onClick={() => navigate(`/user/${id}/map`)}>{t('users.detail.location.more')}
                                    </button>}
                            </div>
                            <Suspense fallback={null}>
                                <UserDetailMap className={'w-full h-full min-h-[213px]'} userID={user?.id} />
                            </Suspense>
                        </div>
                        <div className={'flex justify-between p-[12px] bg-[#353535]'}>
                            <p className={'text-white font-bold'}>{t('users.detail.location.device.status')}</p>
                            {user?.device && recentState?.summary ? (
                                <div className={'flex items-center gap-2'}>
                                    {/* 모든 상태 값이 미사용 조건일 경우 */}
                                    {recentState.summary.hrate === null &&
                                        recentState.summary.hrateStatus === null &&
                                        recentState.summary.respiration === null &&
                                        recentState.summary.respStatus === null &&
                                        recentState.summary.isCharging === false &&
                                        recentState.summary.isOnline === false &&
                                        recentState.summary.step === 0 &&
                                        (recentState.summary.battery === null || recentState.summary.battery === undefined) ? (
                                        <p className={'text-[#ADFF45]'}>{t('users.detail.location.device.status.notUse')}</p>
                                    ) : (
                                        <BatteryStatusBadge
                                            battery={recentState.summary.battery || 0}
                                            status={actualDeviceStatus}
                                            showPercentage={true}
                                            className={'bg-[#353535] border-gray-600'}
                                        />
                                    )}
                                </div>
                            ) : (
                                <p className={'text-[#ADFF45]'}>{t('users.detail.location.device.status.none')}</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* SciChart 기반 통합 생체신호 차트 */}
                <div className={'mt-[15px]'}>
                    <BiometricDashboard
                        className={'w-full'}
                        heartRateData={convertToNumericX(dailyChartData?.hrate)}
                        respirationData={convertToNumericX(dailyChartData?.respiration)}
                        stepData={convertToNumericX(dailyChartData?.step)}
                        fallData={convertToNumericX(dailyChartData?.fall)}
                        realtimeHeartRate={convertToNumericX(realtimeChartData?.hrate)}
                        realtimeRespiration={convertToNumericX(realtimeChartData?.respiration)}
                        realtimeSteps={convertToNumericX(realtimeChartData?.step)}
                        realtimeFalls={convertToNumericX(realtimeChartData?.fall)}
                    />
                </div>
            </>}
        </div>
    )
}

export default UserDetail

